import React, { useCallback, useState } from "react";
import BaseModalWindow from "../BaseModalWindow/BaseModalWindow";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import QueryDB from "@/api/queryDB/common";
import { useNavigate } from "react-router-dom";
import { createToast } from "@/utils/toaster/createToast";
import { BaseTextInput } from "../BaseTextInput/BaseTextInput";
import EmojiContainer from "../EmojiWrapper";
import CloudFunctions from "./../../api/cloudFunctions/CloudFunctions";
import { SinglePostComponent } from "@/features/feed";
import { PostData } from "@/features/feed/types/singlePostsProps";
import {
  Box,
  Button,
  Card,
  Dialog,
  Flex,
  Spinner,
  TextField,
} from "@radix-ui/themes";
import { ImageIcon, FaceIcon } from "@radix-ui/react-icons";
import EmojiWrapper from "@/components/EmojiWrapper/EmojiWrapper";
import EmojiPicker from "emoji-picker-react";

export const ShareContentModalWindow: React.FunctionComponent<{
  closeWindow: () => void;
  show: boolean;
  sharedFrom: "gather" | "team" | "challenge" | "news" | "post";
  createType: "share" | "create";

  additionalData?: any;

  postData: PostData;
}> = ({
  show,
  sharedFrom,
  closeWindow,
  createType,
  additionalData,
  postData,
}) => {
  const [loading, setLoading] = useState(false);
  const currentUser = useCurrentUserProfile((s) => s.user);
  const [shareText, setShareText] = useState("");
  const [emojiOpen, setEmojiOpen] = useState<boolean>(false);
  const navigate = useNavigate();

  const handleClick = useCallback(() => {
    if (!currentUser.uid) {
      navigate("/sign-up-1");
      closeWindow();
      return;
    }
  }, [currentUser.uid, history]);

  const shareExistingPost = async () => {
    createToast({
      type: "info",
      message: "Sharing post...",
    });
    setLoading(false);
    closeWindow();
    await CloudFunctions.sharePost(postData.id, currentUser.uid!, shareText)
      .then(() => {
        createToast({
          type: "success",
          message: "Shared post!",
        });
      })
      .catch(() => {
        createToast({
          type: "error",
          message: "Failed to share post, please try again later!",
        });
      });
  };

  const shareChallengeAsPost = async () => {
    await CloudFunctions.shareChallengeAsPost(
      postData.id,
      currentUser.uid!,
      shareText
    ).then(() => {
      createToast({
        type: "success",
        message: "Shared post!",
      });
      closeWindow();
    });
  };

  const shareItemAsPost = async (itemType: "gather" | "team" | "news") => {
    await QueryDB.createNewDoc("posts", {
      [`${itemType}Id`]: postData.id,
      isVisible: true,
      postText: shareText,
      type: postData.type,
      uid: currentUser.uid!,
      totalSharesNumber: 0,
      postedIn: "feed",
    }).then(() => {
      createToast({
        type: "success",
        message: `Shared ${sharedFrom} to profile and feed!`,
      });
    });
  };
  return (
    <BaseModalWindow
      show={show}
      closeModal={closeWindow}
      size={
        postData.type === "userPost"
          ? "modal--share-post"
          : "modal--share-challenge"
      }
      title={`Share ${sharedFrom} on your profile`}
    >
      <Flex direction="column" gap="3" width="100%">
        <Box position="relative">
          {/* Input Field */}
          <Box>
            <TextField.Root
              placeholder="Write a comment before sharing"
              size="3"
              type="text"
              value={shareText}
              onChange={(e) => {
                setShareText(e.target.value);
              }}
            >
              {/* Emoji button */}
              <TextField.Slot>
                <Button
                  variant="ghost"
                  style={{ width: "100%" }}
                  onClick={() => setEmojiOpen(!emojiOpen)}
                >
                  <FaceIcon color="gray" />
                </Button>
              </TextField.Slot>
            </TextField.Root>
          </Box>

          {/* Emoji picker */}
          <EmojiWrapper
            show={emojiOpen}
            setShow={() => setEmojiOpen(!emojiOpen)}
          >
            <Box
              position="absolute"
              inset="100% auto auto 0"
              style={{ zIndex: 100 }}
            >
              <EmojiPicker
                emojiVersion="0.6"
                lazyLoadEmojis={true}
                onEmojiClick={(emojiObject) => {
                  setShareText((prevText) => prevText + emojiObject.emoji);
                }}
              />
            </Box>
          </EmojiWrapper>
        </Box>

        <Box>
          {postData && (
            <SinglePostComponent
              postData={postData}
              isFullPagePost={true}
              actionButtons={false}
              isInModal
              isFriendsOfCurrentUser={false}
              likeParentId={postData.id}
            />
          )}
        </Box>

        <Flex gap="3" justify="between">
          <Dialog.Close>
            <Button variant="soft">Cancel</Button>
          </Dialog.Close>
          <Dialog.Close>
            <Button
              variant="solid"
              onClick={async () => {
                setLoading(true);

                if (createType === "share") {
                  await shareExistingPost();
                }

                if (createType === "create") {
                  if (sharedFrom === "news") {
                    await shareItemAsPost("news");
                  }

                  if (sharedFrom === "challenge") {
                    await shareChallengeAsPost();
                  }

                  if (sharedFrom === "team") {
                    await shareItemAsPost("team");
                  }

                  if (sharedFrom === "gather") {
                    await shareItemAsPost("gather");
                  }
                }
                setLoading(false);
              }}
              disabled={loading}
            >
              {loading ? <Spinner /> : "Share"}
            </Button>
          </Dialog.Close>
        </Flex>
      </Flex>
    </BaseModalWindow>
  );
};
